import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';
import type { ClientStanding, DischargeAwaiting, HeldClient } from './types.ts';

/**
 * A client's standing now: appointments, the powers held today, and the people
 * on the legal side of the case.
 *
 * Keyed under the client, so recording a court order — which invalidates
 * `['client', id]` — refreshes the authority it may have changed.
 */
export function useClientStanding(clientId: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'standing'],
    queryFn: () => apiFetch<ClientStanding>(`/clients/${clientId}/standing`, getToken),
    ...queryDefaults,
  });
}

/** Every client the load held, waiting on a person (102). */
export function useHeldClients() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['clients', 'held'],
    queryFn: () => apiFetch<{ held: HeldClient[] }>('/clients/held', getToken),
    ...queryDefaults,
  });
}

/** Every discharged client nobody has confirmed (107), and whether this person may. */
export function useDischargeAwaiting() {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['clients', 'discharge-confirmation'],
    queryFn: () => apiFetch<{ awaiting: DischargeAwaiting[]; may_confirm: boolean }>('/clients/discharge-confirmation', getToken),
    ...queryDefaults,
  });
}

/** A person confirms a client's discharge, by their own name and today's date (107). */
export function useConfirmDischarge(clientId: string) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { note?: string }) =>
      apiFetch<unknown>(`/clients/${clientId}/discharge-confirmation`, getToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client', clientId] });
      void queryClient.invalidateQueries({ queryKey: ['clients', 'discharge-confirmation'] });
    },
  });
}
